import { useState, useEffect, useCallback, useRef } from "react";
import type { NewsModel } from "@bloomberg/octagon-shared";
import { newsClient } from "~config/octagon-client";
import { WATCHLIST_NEWS_FAILURE_TEXT } from "~constants/Portfolio";
import type { NewsItem } from "~components/Watchlist/delta/News/NewsList";

const NEWS_FETCH_LIMIT = 20;

function formatRelativeTime(publishedAt: string): string {
  const now = Date.now();
  const published = new Date(publishedAt).getTime();
  const diffMs = now - published;
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;

  return `${diffDays}d ago`;
}

function transformNewsModel(
  model: NewsModel,
  positionsMap: Map<string, number>
): NewsItem | null {
  if (!model.title) return null;

  return {
    id: model.id,
    category: model.primaryCategory,
    headline: model.title,
    imageUrl: model.thumbnailImage,
    // Only show tickers present in the user's watchlist, deduped via Map
    tickers: Array.from(
      new Map(
        (model.tickers ?? [])
          .filter((ticker) => positionsMap.has(ticker.id))
          .map((ticker) => [ticker.id, ticker] as const)
      ).values()
    ).map((ticker) => ({
      symbol: ticker.id,
      percentChange: positionsMap.get(ticker.id) ?? 0,
    })),
    timestamp: model.publishedAt ? formatRelativeTime(model.publishedAt) : "",
    url: model.url,
  };
}

interface UseWatchlistNewsResult {
  readonly items: readonly NewsItem[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly retry: () => void;
}

export function useWatchlistNews(
  ticker: string,
  positionsMap: Map<string, number>
): UseWatchlistNewsResult {
  const [items, setItems] = useState<readonly NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const positionsMapRef = useRef(positionsMap);

  positionsMapRef.current = positionsMap;

  const retry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!ticker) {
      setItems([]);
      setIsLoading(false);
      setError(null);

      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    let cancelled = false;

    setIsLoading(true);
    setError(null);

    const client = newsClient.getInstance();

    client
      .getNewsByTicker({ ticker, limit: NEWS_FETCH_LIMIT })
      .then((newsModels: NewsModel[]) => {
        if (cancelled) return;

        const transformed = newsModels
          .map((model) => transformNewsModel(model, positionsMapRef.current))
          .filter((item): item is NewsItem => item !== null);

        setItems(transformed);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(WATCHLIST_NEWS_FAILURE_TEXT);
        setItems([]);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticker, retryCount]);

  return { items, isLoading, error, retry };
}
